import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../../api/client';
import {
  useAddTeamMember, useEditTeamMember, useTeam, useTeamMemberAction, type TeamMember,
} from '../../api/hooks';
import { useSession } from '../../auth/session';
import { Alert, Button, Chip, Empty, Field, Modal, Note, Panel, Spinner, Table } from '../../ui';

interface Form { firstName: string; lastName: string; email: string; phone: string; roleId: string }
const BLANK: Form = { firstName: '', lastName: '', email: '', phone: '', roleId: '' };
const dash = '—';

/**
 * R-18 / RL-13 — the account's Owner managing their own team: add a person (the server makes a temporary
 * password, shown once), change their name, phone or role, deactivate or reactivate them, and issue a new
 * temporary password. The Owner and the primary contact are shown but cannot be changed here (only Miraki can),
 * and the Owner role is never offered. Every change goes through the API's own checks, whatever this screen shows.
 */
export function TeamPanel() {
  const { t } = useTranslation();
  const { me } = useSession();
  const team = useTeam(true);
  const add = useAddTeamMember();
  const edit = useEditTeamMember();
  const act = useTeamMemberAction();
  const [form, setForm] = useState<Form | null>(null);
  const [editing, setEditing] = useState<TeamMember | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  /** A temporary password, held only until the Owner has seen it. */
  const [secret, setSecret] = useState<{ who: string; password: string } | null>(null);

  if (me?.kind !== 'retailer' || !me.permissions.includes('shop.user_manage')) return null;
  if (team.isLoading) return <Spinner />;
  if (team.error || !team.data) return <Alert tone="stop">{t('shop.account.teamLoadFailed')}</Alert>;
  const { members, roles } = team.data;

  const set = (k: keyof Form, v: string) => setForm((f) => (f ? { ...f, [k]: v } : f));
  const fail = (e: unknown) => setError(e instanceof ApiError ? e.message : t('shop.account.teamFailed'));
  const busy = add.isPending || edit.isPending || act.isPending;
  const valid = !!form && form.firstName.trim() !== '' && form.lastName.trim() !== '' && form.roleId !== ''
    && (editing !== null || /^\S+@\S+\.\S+$/.test(form.email.trim()));

  function begin(m: TeamMember | null) {
    setError(null); setNotice(null); setEditing(m);
    setForm(m
      ? { firstName: m.firstName ?? '', lastName: m.lastName ?? '', email: m.email, phone: m.phone ?? '', roleId: m.roles[0]?.id ?? '' }
      : { ...BLANK, roleId: roles[0]?.id ?? '' });
  }

  function submit() {
    if (!form || !valid || busy) return;
    setError(null);
    const body = {
      firstName: form.firstName.trim(), lastName: form.lastName.trim(), phone: form.phone.trim() || null, roleId: form.roleId,
    };
    if (editing) {
      edit.mutate({ id: editing.id, ...body }, {
        onSuccess: () => { setForm(null); setNotice(t('shop.account.teamSaved', { name: `${body.firstName} ${body.lastName}` })); },
        onError: fail,
      });
    } else {
      add.mutate({ ...body, email: form.email.trim() }, {
        onSuccess: (r) => { setForm(null); setSecret({ who: r.email, password: r.temporaryPassword }); },
        onError: fail,
      });
    }
  }

  function run(m: TeamMember, action: 'deactivate' | 'reactivate' | 'reset-password') {
    setError(null); setNotice(null);
    act.mutate({ id: m.id, action }, {
      onSuccess: (r) => {
        if (r.temporaryPassword) setSecret({ who: m.email, password: r.temporaryPassword });
        else setNotice(t(action === 'deactivate' ? 'shop.account.teamDeactivated' : 'shop.account.teamReactivated', { name: m.name }));
      },
      onError: fail,
    });
  }

  return (
    <Panel
      title={t('shop.account.team')}
      actions={<Button size="sm" onClick={() => begin(null)}>{t('shop.account.teamAdd')}</Button>}
    >
      {error && !form ? <Alert tone="stop">{error}</Alert> : null}
      {notice ? <Alert tone="done">{notice}</Alert> : null}
      <Table<TeamMember>
        rows={members}
        rowKey={(m) => m.id}
        empty={<Empty title={t('shop.account.teamEmpty')} body="" />}
        columns={[
          {
            header: t('shop.account.name'),
            render: (m) => <>{m.name}{m.isYou ? <span className="mk-dim"> ({t('shop.account.teamYou')})</span> : null}</>,
          },
          { header: t('shop.account.email'), render: (m) => m.email },
          { header: t('shop.account.phone'), render: (m) => m.phone ?? dash },
          {
            header: t('shop.account.role'),
            render: (m) => (m.isPrimary ? t('shop.account.rolePrimaryShort') : '') + (m.roles.length
              ? `${m.isPrimary ? ' · ' : ''}${m.roles.map((r) => r.name).join(', ')}` : (m.isPrimary ? '' : t('shop.account.teamNoRole'))),
          },
          {
            header: t('shop.account.status'),
            render: (m) => <Chip tone={m.isActive ? 'done' : 'lapse'}>{m.isActive ? t('shop.account.teamActive') : t('shop.account.teamInactive')}</Chip>,
          },
          {
            header: '',
            render: (m) => (m.locked ? <span className="mk-dim">{t('shop.account.teamLocked')}</span> : (
              <div style={{ display: 'flex', gap: 'var(--mk-space-2)', flexWrap: 'wrap' }}>
                <Button size="sm" tone="ghost" disabled={busy} onClick={() => begin(m)}>{t('shop.account.teamEdit')}</Button>
                {m.isActive
                  ? <Button size="sm" tone="ghost" disabled={busy} onClick={() => run(m, 'deactivate')}>{t('shop.account.teamDeactivate')}</Button>
                  : <Button size="sm" tone="ghost" disabled={busy} onClick={() => run(m, 'reactivate')}>{t('shop.account.teamReactivate')}</Button>}
                {m.isActive
                  ? <Button size="sm" tone="ghost" disabled={busy} onClick={() => run(m, 'reset-password')}>{t('shop.account.teamResetPassword')}</Button>
                  : null}
              </div>
            )),
          },
        ]}
      />
      <Note>{t('shop.account.teamOwnerNote')}</Note>

      {form ? (
        <Modal
          title={editing ? t('shop.account.teamEditTitle') : t('shop.account.teamAddTitle')}
          onClose={() => { if (!busy) setForm(null); }}
          footer={(
            <>
              <Button tone="ghost" disabled={busy} onClick={() => setForm(null)}>{t('common.cancel')}</Button>
              <Button disabled={!valid || busy} onClick={submit}>
                {busy ? t('shop.account.teamSaving') : editing ? t('shop.account.teamSave') : t('shop.account.teamCreate')}
              </Button>
            </>
          )}
        >
          {!editing ? <p>{t('shop.account.teamAddIntro')}</p> : null}
          <Field label={t('shop.account.teamFirstName')}><input className="mk-fld" maxLength={80} value={form.firstName} onChange={(e) => set('firstName', e.target.value)} /></Field>
          <Field label={t('shop.account.teamLastName')}><input className="mk-fld" maxLength={80} value={form.lastName} onChange={(e) => set('lastName', e.target.value)} /></Field>
          <Field label={t('shop.account.email')} hint={editing ? t('shop.account.teamEmailFixed') : undefined}>
            <input className="mk-fld" type="email" maxLength={254} disabled={editing !== null} value={form.email} onChange={(e) => set('email', e.target.value)} />
          </Field>
          <Field label={t('shop.account.phone')}><input className="mk-fld" maxLength={40} value={form.phone} onChange={(e) => set('phone', e.target.value)} /></Field>
          <Field label={t('shop.account.role')}>
            <select value={form.roleId} onChange={(e) => set('roleId', e.target.value)}>
              {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </Field>
          {error ? <div role="alert" className="mk-dim">{error}</div> : null}
        </Modal>
      ) : null}

      {secret ? (
        <Modal
          title={t('shop.account.teamPasswordTitle')}
          onClose={() => setSecret(null)}
          footer={<Button onClick={() => setSecret(null)}>{t('shop.account.teamPasswordDone')}</Button>}
        >
          <p>{t('shop.account.teamPasswordBody', { email: secret.who })}</p>
          <p><code style={{ fontSize: 18, userSelect: 'all' }}>{secret.password}</code></p>
          <Note>{t('shop.account.teamPasswordNote')}</Note>
        </Modal>
      ) : null}
    </Panel>
  );
}
